// src/components/ChannelSettings.tsx
import React, { useState, useEffect } from 'react';
import '../styles/ChannelSettings.css';
import Button from './Button';
import ToggleButton from './ToggleButton';
import { updateChannelInfo } from '../services/channelService';
import { useChannelInfo } from '../hooks/useChannelInfo';
import { updateCurrentUser } from '../services/userService';
import Modal from './Modal';
import { NotificationInfo } from '../types/NotificationInfo';
import LoadingIndicator from './LoadingIndicator';

interface ChannelSettingsProps {
  user: any;
  setNotification: (notification: NotificationInfo) => void;
  // 最新のユーザー情報を再取得してグローバル状態を更新するための関数
  refreshUser: () => void;
}

const ChannelSettings: React.FC<ChannelSettingsProps> = ({
  user,
  setNotification,
  refreshUser,
}) => {
  const {
    data: channelInfo,
    isLoading: isChannelLoading,
    isError: isChannelError,
    error: channelError,
    refetch: refetchChannelInfo,
  } = useChannelInfo();

  const [formValues, setFormValues] = useState({
    line_channel_id: '',
    line_access_token: '',
    line_channel_secret: '',
    autoResponseEnabled: false,
    autoResponsePrompt: '',
  });
  const [saving, setSaving] = useState(false);

  // モーダル用状態
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [onConfirmAction, setOnConfirmAction] = useState<() => void>(() => { });

  useEffect(() => {
    if (channelInfo && user) {
      setFormValues({
        line_channel_id: channelInfo.line_channel_id ?? '',
        line_access_token: channelInfo.line_access_token ?? '',
        line_channel_secret: channelInfo.line_channel_secret ?? '',
        autoResponseEnabled: !!user.auto_response_enabled,
        autoResponsePrompt:
          user.auto_response_prompt
          // フォールバック（移行期間中のみ有効にしておくと親切）
          ?? user.auto_response_prompt_premium
          ?? user.auto_response_prompt_free
          ?? '',
      });
    }
  }, [channelInfo, user]);

  // ② user が null のまま来ることをガード
  if (!user) {
    return <LoadingIndicator isFullPage size={60} />;
  }

  // ③ チャネル取得時のエラーをハンドリング（たとえば 401 ）
  if (isChannelError) {
    console.error('ChannelInfo error:', channelError);
    return (
      <div>チャネル情報の取得に失敗しました。再度ログインしてください。</div>
    );
  }

  if (isChannelLoading) {
    return <LoadingIndicator isFullPage size={60} />;
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = e.target;
    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      setFormValues((prev) => ({
        ...prev,
        [target.name]: target.checked,
      }));
    } else {
      setFormValues((prev) => ({
        ...prev,
        [target.name]: target.value,
      }));
    }
  };

  // 確認モーダルを開くための共通関数
  const openConfirmModal = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setOnConfirmAction(() => action);
    setConfirmModalOpen(true);
  };

  // 更新処理を確認モーダル経由で実行
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    openConfirmModal('本当に更新してよろしいですか？', async () => {
      setSaving(true);
      try {
        await updateChannelInfo({
          line_channel_id: formValues.line_channel_id,
          line_access_token: formValues.line_access_token,
          line_channel_secret: formValues.line_channel_secret,
        });
        await refetchChannelInfo();
        await updateCurrentUser({
          auto_response_enabled: formValues.autoResponseEnabled,
          auto_response_prompt: formValues.autoResponsePrompt.substring(0, 200),
          // 旧項目は送らない（サーバが受け付ける間だけ送ってもOKだが早めに撤去）
        });
        // 更新成功後、最新のユーザー情報を再取得する
        refreshUser();
        setNotification({
          message: '設定が更新されました。',
          bgColor: '#28a745', // 成功時は緑
          textColor: '#fff',
        });
      } catch (err) {
        console.error(err);
        setNotification({
          message: '設定の更新に失敗しました。',
          bgColor: '#dc3545', // 失敗時は赤
          textColor: '#fff',
        });
      }
      setSaving(false);
      setConfirmModalOpen(false);
    });
  };

  return (
    <div className="channel-section">
      <h2 className="section-title">LINEチャネルおよびチャット設定</h2>
      <a href="/instruction" target="_blank" rel="noreferrer">設定マニュアルはこちら</a>
      <form onSubmit={handleSubmit}>
        <h3 className="section-sub-title">公式ライン連携</h3>
        <div className="channel-field">
          <label>LINEチャネルID</label>
          <input
            type="text"
            name="line_channel_id"
            value={formValues.line_channel_id}
            onChange={handleChange}
            required
            title="LINEチャネルID"
            placeholder="LINEチャネルID"
          />
        </div>
        <div className="channel-field">
          <label>LINEアクセストークン</label>
          <input
            type="text"
            name="line_access_token"
            value={formValues.line_access_token}
            onChange={handleChange}
            required
            title="LINEアクセストークン"
            placeholder="LINEアクセストークン"
          />
        </div>
        <div className="channel-field">
          <label>LINEチャネルシークレット</label>
          <input
            type="text"
            name="line_channel_secret"
            value={formValues.line_channel_secret}
            onChange={handleChange}
            required
            title="LINEチャネルシークレット"
            placeholder="LINEチャネルシークレット"
          />
        </div>
        <div className="channel-field">
          <label>Webhook URL</label>
          {formValues.line_channel_id && (
            <div className="webhook-url">
              <span className="webhook-url__text">
                {`https://api.auto-chat.ai/line/webhook/${formValues.line_channel_id}/`}
              </span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `https://api.auto-chat.ai/line/webhook/${formValues.line_channel_id}/`
                  );
                  setNotification({
                    message: 'Webhook URLをコピーしました',
                    bgColor: '#17a2b8',
                    textColor: '#fff',
                  });
                }}
              >
                コピー
              </button>
            </div>
          )}
        </div>
        <h3 className="section-sub-title">自動応答設定</h3>
        <div className="channel-field">
          <label>AIによる自動応答を実行</label>
          <ToggleButton
            isOn={formValues.autoResponseEnabled}
            onToggle={() =>
              setFormValues((prev) => ({
                ...prev,
                autoResponseEnabled: !prev.autoResponseEnabled,
              }))
            }
          />
        </div>

        <div className="channel-field">
          <label>
            AI自動応答用プロンプト
            <br />
            (200文字以内)
          </label>
          <textarea
            name="autoResponsePrompt"
            value={formValues.autoResponsePrompt}
            onChange={handleChange}
            maxLength={200}
            title="AI自動応答用プロンプト"
            placeholder="AI自動応答用プロンプト (200文字以内)"
            rows={4}
          />
        </div>
        <Button type="submit" disabled={saving}>
          {saving ? '更新中...' : '更新する'}
        </Button>
      </form>
      {confirmModalOpen && (
        <Modal
          isOpen={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
        >
          <h2>確認</h2>
          <p>{confirmMessage}</p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '1rem',
            }}
          >
            <Button
              onClick={() => setConfirmModalOpen(false)}
              bgColor="#6c757d"
              textColor="#fff"
            >
              キャンセル
            </Button>
            <Button
              onClick={onConfirmAction}
              style={{ marginLeft: '0.5rem' }}
              bgColor="#28a745"
              textColor="#fff"
            >
              実行する
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ChannelSettings;
